import React, { FC, Suspense, lazy, useEffect } from "react";
import "./App.scss";
import { Navigate, Route, Routes } from "react-router-dom";
import {
    MsalProvider,
    useIsAuthenticated,
    AuthenticatedTemplate,
    UnauthenticatedTemplate,
    useMsal,
} from "@azure/msal-react";

const Summary = lazy(() => import("./Pages/Summary/Summary"));
const Karte = lazy(() => import("./Pages/Karte/Karte"));
const Hub = lazy(() => import("./Pages/Hub/Hub"));
import Navigation from "./Navigation/Navigation";
import { getGroup } from "../Auth/claimsUtil";
import useAppStore from "./Store/AppStore";
import ModalPopup from "./Popups/ModalWrapper";
import {
    setFeatureLayer,
    setViewPort,
    setMapTopic,
    setKeyVaultSecret,
    setFeatureFlag
} from '../service/apiService';
import useMapStore from "./Store/MapStore";
import { InteractionStatus, IPublicClientApplication, PublicClientApplication } from "@azure/msal-browser";
import { loginRequest } from "../Auth/authConfig";


interface AppProps {
    msalInstance: PublicClientApplication;
}

type ArcGisOAuthConfig = {
    appId: string;
    portalUrl: string;
};

type Infra3dLoginConfig = {
    infra3dUser: string;
    infra3dLogin: string;
};

const MainContent: FC = () => {
    const { instance, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const setCurrentPopup = useAppStore((state) => state.setCurrentPopup);
    const setModalPopupIsOpen = useAppStore((state) => state.setModalPopupIsOpen);

    const isArcGisOAuthConfig = (value: unknown): value is ArcGisOAuthConfig => {
        return (
            typeof value === "object" &&
            value !== null &&
            "appId" in value &&
            "portalUrl" in value &&
            typeof (value as ArcGisOAuthConfig).appId === "string" &&
            typeof (value as ArcGisOAuthConfig).portalUrl === "string"
        );
    };

    const isInfra3dLoginConfig = (value: unknown): value is Infra3dLoginConfig => {
        return (
            typeof value === "object" &&
            value !== null &&
            "infra3dUser" in value &&
            "infra3dLogin" in value &&
            typeof (value as Infra3dLoginConfig).infra3dUser === "string" &&
            typeof (value as Infra3dLoginConfig).infra3dLogin === "string"
        );
    };

    useEffect(() => {
        if (useAppStore.getState().environment == "Showcase") return;

        const accounts = instance.getAllAccounts();
        if (!isAuthenticated && inProgress === InteractionStatus.None && accounts.length === 0) {
            instance.loginRedirect(loginRequest);
            return;
        }

        if (!isAuthenticated && accounts.length > 0) {
            instance.setActiveAccount(accounts[0]);
        }
    }, [isAuthenticated, inProgress, instance]);

    useEffect(() => {
        if (isAuthenticated) {
            getGroup().then((groupPromise) => {
                useAppStore.getState().setGroup(groupPromise);

                if (groupPromise !== "") {
                    setCurrentPopup("");
                    setModalPopupIsOpen(false);
                } else {
                    setCurrentPopup("noGroupAssigned");
                    setModalPopupIsOpen(true);
                }
            });
        }
    }, [isAuthenticated, setCurrentPopup, setModalPopupIsOpen]);

    useEffect(() => {
        const initializeArcGIS = async () => {
            //Fetches OAuth info for ArcGis from keyVault
            if (useAppStore.getState().environment !== "Showcase") {
                if (!isAuthenticated) {
                    return;
                }
                try {
                    const [{ default: OAuthInfo }, { default: IdentityManager }] =
                        await Promise.all([
                            import("@arcgis/core/identity/OAuthInfo"),
                            import("@arcgis/core/identity/IdentityManager"),
                        ]);

                    const data = await setKeyVaultSecret("OAUTHINFO-ESRI");

                    if (!isArcGisOAuthConfig(data)) {
                        console.error("Invalid ArcGIS OAuth configuration:", data);
                        return;
                    }
                    useAppStore.getState().setArcGisPortalUrl(data.portalUrl);
                    const oAuthInfo = new OAuthInfo({
                        appId: data.appId,
                        popup: false,
                        portalUrl: data.portalUrl,
                        preserveUrlHash: true,
                    });
                    IdentityManager.registerOAuthInfos([oAuthInfo]);
                    try {

                        await IdentityManager.checkSignInStatus(oAuthInfo.portalUrl);
                        changeOAuthStorage();
                    } catch (signInError) {
                        console.warn("ArcGIS sign-in status check failed:", signInError);
                        // Continue execution as this might not be critical
                    }

                } catch (error) {
                    console.error("ArcGIS initialization error:", error);
                }
            }
        };

        initializeArcGIS();
    }, [isAuthenticated]);

    useEffect(() => {
        const shouldInitializeAppData =
            useAppStore.getState().environment === "Showcase" ||
            isAuthenticated;

        if (!shouldInitializeAppData) {
            return;
        }

        const initializeAppData = async () => {
            try {
                const infra3dData = await setKeyVaultSecret("INFRA3D-LOGIN");

                if (isInfra3dLoginConfig(infra3dData)) {
                    useAppStore.getState().setInfra3dLogin(
                        infra3dData.infra3dUser,
                        infra3dData.infra3dLogin
                    );
                } else {
                    console.error("Missing Infra3D credentials:", infra3dData);
                }

                await Promise.allSettled([
                    setFeatureFlag(),
                    setViewPort()
                ]);

                const featureLayerData = await setFeatureLayer();

                if (featureLayerData?.length) {
                    useAppStore.getState().setYearFilter(
                        featureLayerData[featureLayerData.length - 1].id
                    );
                }

                const mapTopicData = await setMapTopic();

                if (mapTopicData?.length) {
                    const selectedYear = useAppStore.getState().yearFilterValues.find(
                        (yearFilter) => yearFilter.selected
                    );

                    const firstVisibleTopic =
                        selectedYear?.mapTopics?.length
                            ? mapTopicData.find((topic) =>
                                selectedYear.mapTopics.includes(topic.label)
                            )
                            : mapTopicData[0];

                    useMapStore.getState().selectMapTopic(
                        firstVisibleTopic?.id ?? mapTopicData[0].id
                    );
                }
            } catch (error) {
                console.error("Error initializing app data:", error);
            }
        };

        initializeAppData();
    }, [isAuthenticated]);

    return (
        <div className="App">
            {useAppStore.getState().environment !== 'Showcase' ? (
                <div>
                    <AuthenticatedTemplate>
                        <ModalPopup/>
                        <Navigation/>
                        <main className="absolute w-full h-full overflow-hidden">
                            <Suspense fallback={null}>
                                <Routes>
                                    <Route index element={<Navigate to="karte"/>}/>
                                    <Route path="/karte" element={<Karte/>}/>
                                    <Route path="/summary" element={<Summary/>}/>
                                    <Route path="/hub" element={<Hub/>}/>
                                </Routes>
                            </Suspense>
                        </main>
                    </AuthenticatedTemplate>
                    <UnauthenticatedTemplate>
                        <Suspense fallback={null}>
                            <Routes>
                                <Route path="" element={
                                                    <SignUpSignInRoute/>
                                               }/>
                            </Routes>
                        </Suspense>
                    </UnauthenticatedTemplate>
                </div>
            ) : (
                <div>
                    <ModalPopup/>
                    <Navigation/>
                    <main className="absolute w-full h-full overflow-hidden">
                        <Suspense fallback={null}>
                            <Routes>
                                <Route index element={<Navigate to="karte"/>}/>
                                <Route path="/karte" element={<Karte/>}/>
                                <Route path="/summary" element={<Summary/>}/>
                                <Route path="/hub" element={<Hub/>}/>
                            </Routes>
                        </Suspense>
                    </main>
                </div>
            )}
        </div>
    );
};

const App: FC<AppProps> = ({ msalInstance }) => {
    return (
        <MsalProvider instance={msalInstance}>
            <MainContent/>
        </MsalProvider>
    );
};
export default App;

const SignUpSignInRoute = () => {
    const { instance, inProgress, accounts } = useMsal();
    const isAuthenticated = accounts.length > 0;

    return (
        <SignUp_SignIn
            instance={instance}
            inProgress={inProgress}
            isAuthenticated={isAuthenticated}
        />
    );
};

type SignUpSignInProps = {
    instance: IPublicClientApplication;
    inProgress: InteractionStatus;
    isAuthenticated: boolean;
};
const SignUp_SignIn = ({ instance, inProgress, isAuthenticated }: SignUpSignInProps) => {
    useEffect(() => {
        if (isAuthenticated || inProgress !== InteractionStatus.None) {
            return;
        }

        const accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
            instance.setActiveAccount(accounts[0]);
        }
    }, [instance, inProgress, isAuthenticated]);

    return null;
};

//Takes the esri OAuth Token and moves it from session to local storage. This way the login is retained between sessions
function changeOAuthStorage() {
    const key = "esriJSAPIOAuth";
    if (window && window.sessionStorage) {
        const oauth = window.sessionStorage.getItem(key);
        if (!oauth) {
            return;
        }
        const oauthObj = JSON.parse(oauth);
        if (window.localStorage) {
            const localStorageOAuths = window.localStorage.getItem(key);
            if (!localStorageOAuths) {
                window.localStorage.setItem(key, JSON.stringify(oauthObj));
                window.sessionStorage.removeItem(key);
                return;
            }
            const lsAuth = JSON.parse(localStorageOAuths);
            lsAuth["/"] = { ...lsAuth["/"], ...oauthObj["/"] };
            window.localStorage.setItem(key, JSON.stringify(lsAuth));
            window.sessionStorage.removeItem(key);
        }
    }
}
